import { useState, useEffect } from "react";
import { Icon } from "@juiceforward/narrative-player";

interface MockNarrative {
  id: string;
  title: string;
  variationsTotal: number;
  status: "complete" | "processing" | "draft";
  processingPercent?: number;
  lastModified: string;
  author: string;
  views: number;
}

interface MockCustomerVariation {
  customerId: string;
  customerName: string;
  status: "generated" | "flagged" | "pending";
  lastGenerated: string;
  views: number;
}

const mockNarratives: MockNarrative[] = [
  {
    id: "narrative_001",
    title: "Family Engagement Progress Report",
    variationsTotal: 12,
    status: "complete",
    author: "Jennifer Martinez",
    lastModified: "2026-02-15",
    views: 2340,
  },
  {
    id: "narrative_002",
    title: "Smart Insights 2026Year in Review",
    variationsTotal: 8,
    status: "processing",
    processingPercent: 45,
    author: "Michael Thompson",
    lastModified: "2026-02-18",
    views: 1456,
  },
  {
    id: "narrative_003",
    title: "GOLD Assessment Insights",
    variationsTotal: 15,
    status: "complete",
    author: "Amanda Chen",
    lastModified: "2026-02-10",
    views: 3201,
  },
  {
    id: "narrative_004",
    title: "Classroom Learning Outcomes Review",
    variationsTotal: 20,
    status: "draft",
    author: "David Rodriguez",
    lastModified: "2026-02-01",
    views: 456,
  },
];

const mockCustomerVariations: Record<string, MockCustomerVariation[]> = {
  narrative_001: [
    {
      customerId: "SCHL_001",
      customerName: "Bright Beginnings Learning Center",
      status: "generated",
      lastGenerated: "2026-02-15",
      views: 234,
    },
    {
      customerId: "SCHL_002",
      customerName: "Little Learners Academy",
      status: "flagged",
      lastGenerated: "2026-02-18",
      views: 12,
    },
    {
      customerId: "SCHL_003",
      customerName: "Creative Kids Preschool",
      status: "generated",
      lastGenerated: "2026-02-10",
      views: 456,
    },
  ],
  narrative_002: [
    {
      customerId: "SCHL_004",
      customerName: "Sunshine Early Education",
      status: "generated",
      lastGenerated: "2026-02-01",
      views: 789,
    },
    {
      customerId: "SCHL_005",
      customerName: "Growing Minds Childcare",
      status: "pending",
      lastGenerated: "2026-02-17",
      views: 5,
    },
  ],
};

interface NarrativesViewProps {
  onNewNarrativeClick?: () => void;
  onExportManifestClick?: () => void;
  onDrillDownChange?: (isDrilledDown: boolean) => void;
  onViewEngagement?: () => void;
}

export const NarrativesView = ({
  onExportManifestClick,
  onDrillDownChange,
  onViewEngagement,
}: NarrativesViewProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"date" | "views" | "title">("date");
  const [selectedNarrative, setSelectedNarrative] = useState<string | null>(
    null,
  );
  const [showNewNarrativeModal, setShowNewNarrativeModal] = useState(false);

  // Notify parent of drill-down state changes
  useEffect(() => {
    if (onDrillDownChange) {
      onDrillDownChange(selectedNarrative !== null);
    }
  }, [selectedNarrative, onDrillDownChange]);

  const filteredNarratives = mockNarratives
    .filter((narrative) => {
      const matchesSearch = narrative.title
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus =
        filterStatus === "all" || narrative.status === filterStatus;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === "date") {
        return (
          new Date(b.lastModified).getTime() -
          new Date(a.lastModified).getTime()
        );
      }
      if (sortBy === "views") {
        return b.views - a.views;
      }
      return a.title.localeCompare(b.title);
    });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "complete":
        return "bg-emerald-500/20 text-emerald-400";
      case "processing":
        return "bg-blue-500/20 text-blue-400";
      case "draft":
        return "bg-yellow-500/20 text-yellow-400";
      case "generated":
        return "bg-emerald-500/20 text-emerald-400";
      case "flagged":
        return "bg-red-500/20 text-red-400";
      case "pending":
        return "bg-gray-500/20 text-gray-400";
      default:
        return "bg-gray-500/20 text-gray-400";
    }
  };

  const getStatusDisplay = (narrative: MockNarrative) => {
    if (
      narrative.status === "processing" &&
      narrative.processingPercent !== undefined
    ) {
      return `${narrative.processingPercent}% Processing`;
    }
    return narrative.status.charAt(0).toUpperCase() + narrative.status.slice(1);
  };

  // If a narrative is selected, show customer variations
  if (selectedNarrative) {
    const narrative = mockNarratives.find((n) => n.id === selectedNarrative);
    const customerVariations = mockCustomerVariations[selectedNarrative] || [];

    // Calculate metrics
    const totalVariations = customerVariations.length;
    const generatedCount = customerVariations.filter(
      (v) => v.status === "generated",
    ).length;
    const flaggedCount = customerVariations.filter(
      (v) => v.status === "flagged",
    ).length;
    const viewedCount = customerVariations.filter((v) => v.views > 0).length;

    const percentGenerated =
      totalVariations > 0
        ? Math.round((generatedCount / totalVariations) * 100)
        : 0;
    const percentFlagged =
      totalVariations > 0
        ? Math.round((flaggedCount / totalVariations) * 100)
        : 0;
    const percentViewed =
      totalVariations > 0
        ? Math.round((viewedCount / totalVariations) * 100)
        : 0;

    return (
      <div className="space-y-6">
        {/* Back button and narrative title */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSelectedNarrative(null)}
            className="text-[var(--admin-text-muted)] hover:text-[var(--admin-text)] transition-colors"
          >
            <Icon icon="arrow-left" className="mr-2" />
            Back to Narratives
          </button>
          <div className="flex-1">
            <h2 className="text-2xl font-bold">{narrative?.title}</h2>
            <p className="text-[var(--admin-text-muted)] text-sm">
              {customerVariations.length} customer variations
            </p>
          </div>
          <div className="flex items-center gap-3">
            {onExportManifestClick && (
              <button
                onClick={onExportManifestClick}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors text-sm font-medium"
                title="Export Manifest"
              >
                <Icon icon="arrow-up-from-line" className="mr-2" />
                Export Manifest
              </button>
            )}
            {onViewEngagement && (
              <button
                onClick={onViewEngagement}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors text-sm font-medium"
                title="View Engagement"
              >
                <Icon icon="chart-simple" className="mr-2" />
                View Engagement
              </button>
            )}
          </div>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-5">
            <div className="text-[var(--admin-text-muted)] text-sm mb-2">
              Total Variations
            </div>
            <div className="text-3xl font-bold">{totalVariations}</div>
          </div>
          <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-5">
            <div className="text-[var(--admin-text-muted)] text-sm mb-2">
              % Generated
            </div>
            <div className="text-3xl font-bold text-emerald-400">
              {percentGenerated}%
            </div>
            <div className="text-xs text-[var(--admin-text-subtle)] mt-1">
              {generatedCount} of {totalVariations}
            </div>
          </div>
          <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-5">
            <div className="text-[var(--admin-text-muted)] text-sm mb-2">
              % Flagged
            </div>
            <div className="text-3xl font-bold text-red-400">
              {percentFlagged}%
            </div>
            <div className="text-xs text-[var(--admin-text-subtle)] mt-1">
              {flaggedCount} of {totalVariations}
            </div>
          </div>
          <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-5">
            <div className="text-[var(--admin-text-muted)] text-sm mb-2">
              % Viewed
            </div>
            <div className="text-3xl font-bold text-blue-400">
              {percentViewed}%
            </div>
            <div className="text-xs text-[var(--admin-text-subtle)] mt-1">
              {viewedCount} of {totalVariations}
            </div>
          </div>
        </div>

        {/* Narrative Configuration Info */}
        <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Icon icon="cog" className="text-[var(--admin-text-muted)]" />
            Narrative Configuration
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-xs text-[var(--admin-text-subtle)] uppercase mb-1">
                Expiration Date
              </div>
              <div className="font-medium">March 31, 2026</div>
            </div>
            <div>
              <div className="text-xs text-[var(--admin-text-subtle)] uppercase mb-1">
                Total Data Values
              </div>
              <div className="font-medium">247 metrics</div>
            </div>
            <div>
              <div className="text-xs text-[var(--admin-text-subtle)] uppercase mb-1">
                Total Possible Sections
              </div>
              <div className="font-medium">12 sections</div>
            </div>
            <div>
              <div className="text-xs text-[var(--admin-text-subtle)] uppercase mb-1">
                Created Date
              </div>
              <div className="font-medium">January 15, 2026</div>
            </div>
            <div>
              <div className="text-xs text-[var(--admin-text-subtle)] uppercase mb-1">
                Last Updated
              </div>
              <div className="font-medium">
                {narrative?.lastModified
                  ? new Date(narrative.lastModified).toLocaleDateString()
                  : "N/A"}
              </div>
            </div>
            <div>
              <div className="text-xs text-[var(--admin-text-subtle)] uppercase mb-1">
                Status
              </div>
              <div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                    narrative?.status || "draft",
                  )}`}
                >
                  {narrative ? getStatusDisplay(narrative) : "Unknown"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Customer variations table */}
        <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-[var(--admin-surface-raised)] border-b border-[var(--admin-border)]">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-medium text-[var(--admin-text-muted)]">
                  Customer ID
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[var(--admin-text-muted)]">
                  Customer Name
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[var(--admin-text-muted)]">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[var(--admin-text-muted)]">
                  Last Generated
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[var(--admin-text-muted)]">
                  Views
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[var(--admin-text-muted)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {customerVariations.map((variation) => (
                <tr
                  key={variation.customerId}
                  className="border-b border-[var(--admin-border)] hover:bg-[var(--admin-surface-raised)] transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="font-medium font-mono text-sm">
                      {variation.customerId}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-[var(--admin-text-muted)]">
                    {variation.customerName}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        variation.status,
                      )}`}
                    >
                      {variation.status.charAt(0).toUpperCase() +
                        variation.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[var(--admin-text-muted)]">
                    {new Date(variation.lastGenerated).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-[var(--admin-text-muted)]">
                    {variation.views}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                        title="View"
                      >
                        <Icon icon="eye" />
                      </button>
                      <button
                        className="text-red-400 hover:text-red-300 transition-colors"
                        title="Delete"
                      >
                        <Icon icon="trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {customerVariations.length === 0 && (
            <div className="text-center py-12 text-[var(--admin-text-subtle)]">
              <Icon icon="inbox" className="text-4xl mb-3" />
              <p>No customer variations found</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default view: Narratives list
  return (
    <div className="space-y-6">
      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Icon
              icon="search"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--admin-text-subtle)]"
            />
            <input
              type="text"
              placeholder="Search narratives..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[var(--admin-surface)] border border-[var(--admin-border-secondary)] rounded-lg pl-10 pr-4 py-2.5 text-[var(--admin-text)] placeholder-[var(--admin-text-subtle)] focus:outline-none focus:border-emerald-500"
            />
          </div>
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-[var(--admin-surface)] border border-[var(--admin-border-secondary)] rounded-lg px-4 py-2.5 text-[var(--admin-text)] focus:outline-none focus:border-emerald-500"
        >
          <option value="all">All Status</option>
          <option value="complete">Complete</option>
          <option value="processing">Processing</option>
          <option value="draft">Draft</option>
        </select>
        <select
          value={sortBy}
          onChange={(e) =>
            setSortBy(e.target.value as "date" | "views" | "title")
          }
          className="bg-[var(--admin-surface)] border border-[var(--admin-border-secondary)] rounded-lg px-4 py-2.5 text-[var(--admin-text)] focus:outline-none focus:border-emerald-500"
        >
          <option value="date">Sort by Date</option>
          <option value="views">Sort by Views</option>
          <option value="title">Sort by Title</option>
        </select>
      </div>

      {/* Narratives Table */}
      <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-[var(--admin-surface-raised)] border-b border-[var(--admin-border)]">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-[var(--admin-text-muted)]">
                Title
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-[var(--admin-text-muted)]">
                Variations Total
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-[var(--admin-text-muted)]">
                Status
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-[var(--admin-text-muted)]">
                Last Generated
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-[var(--admin-text-muted)]">
                Views
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-[var(--admin-text-muted)]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredNarratives.map((narrative) => (
              <tr
                key={narrative.id}
                className="border-b border-[var(--admin-border)] hover:bg-[var(--admin-surface-raised)] transition-colors cursor-pointer"
                onClick={() => setSelectedNarrative(narrative.id)}
              >
                <td className="px-6 py-4">
                  <div className="font-medium">{narrative.title}</div>
                  <div className="text-sm text-[var(--admin-text-subtle)]">
                    {narrative.id}
                  </div>
                </td>
                <td className="px-6 py-4 text-[var(--admin-text-muted)]">
                  {narrative.variationsTotal}
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                      narrative.status,
                    )}`}
                  >
                    {getStatusDisplay(narrative)}
                  </span>
                </td>
                <td className="px-6 py-4 text-[var(--admin-text-muted)]">
                  {new Date(narrative.lastModified).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-[var(--admin-text-muted)]">
                  {narrative.views.toLocaleString()}
                </td>
                <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    <button
                      className="text-emerald-400 hover:text-emerald-300 transition-colors"
                      title="Edit"
                    >
                      <Icon icon="pen" />
                    </button>
                    <button
                      className="text-[var(--admin-text-muted)] hover:text-[var(--admin-text)] transition-colors"
                      title="Duplicate"
                    >
                      <Icon icon="copy" />
                    </button>
                    <button
                      className="text-red-400 hover:text-red-300 transition-colors"
                      title="Delete"
                    >
                      <Icon icon="trash" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredNarratives.length === 0 && (
          <div className="text-center py-12 text-[var(--admin-text-subtle)]">
            <Icon icon="inbox" className="text-4xl mb-3" />
            <p>No narratives found</p>
          </div>
        )}
      </div>

      {/* New Narrative Modal */}
      {showNewNarrativeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--admin-border)] flex items-center justify-between">
              <h2 className="text-xl font-semibold">New Narrative</h2>
              <button
                onClick={() => setShowNewNarrativeModal(false)}
                className="text-[var(--admin-text-muted)] hover:text-[var(--admin-text)] transition-colors"
              >
                <Icon icon="times" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--admin-text-muted)] mb-2">
                  Narrative Title
                </label>
                <input
                  type="text"
                  placeholder="e.g., Q1 2026 Executive Summary"
                  className="w-full bg-[var(--admin-input-bg)] border border-[var(--admin-border-secondary)] rounded-lg px-4 py-2.5 text-[var(--admin-text)] placeholder-[var(--admin-text-subtle)] focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--admin-text-muted)] mb-2">
                  Project Goals & Requirements
                </label>
                <textarea
                  rows={4}
                  placeholder="Describe the key goals, objectives, and requirements for this narrative..."
                  className="w-full bg-[var(--admin-input-bg)] border border-[var(--admin-border-secondary)] rounded-lg px-4 py-2.5 text-[var(--admin-text)] placeholder-[var(--admin-text-subtle)] focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--admin-text-muted)] mb-2">
                  Design Direction
                </label>
                <textarea
                  rows={3}
                  placeholder="Describe the tone, style, and visual direction for this narrative..."
                  className="w-full bg-[var(--admin-input-bg)] border border-[var(--admin-border-secondary)] rounded-lg px-4 py-2.5 text-[var(--admin-text)] placeholder-[var(--admin-text-subtle)] focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--admin-text-muted)] mb-2">
                  Target Audience
                </label>
                <input
                  type="text"
                  placeholder="e.g., C-Suite executives, Product managers"
                  className="w-full bg-[var(--admin-input-bg)] border border-[var(--admin-border-secondary)] rounded-lg px-4 py-2.5 text-[var(--admin-text)] placeholder-[var(--admin-text-subtle)] focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--admin-text-muted)] mb-2">
                  Key Metrics to Highlight
                </label>
                <input
                  type="text"
                  placeholder="e.g., Revenue growth, Customer satisfaction, Usage trends"
                  className="w-full bg-[var(--admin-input-bg)] border border-[var(--admin-border-secondary)] rounded-lg px-4 py-2.5 text-[var(--admin-text)] placeholder-[var(--admin-text-subtle)] focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--admin-text-muted)] mb-2">
                  Content Themes
                </label>
                <input
                  type="text"
                  placeholder="e.g., Success stories, Product updates, Strategic insights"
                  className="w-full bg-[var(--admin-input-bg)] border border-[var(--admin-border-secondary)] rounded-lg px-4 py-2.5 text-[var(--admin-text)] placeholder-[var(--admin-text-subtle)] focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>
            <div className="p-6 border-t border-[var(--admin-border)] flex items-center gap-3 justify-end">
              <button
                onClick={() => setShowNewNarrativeModal(false)}
                className="px-4 py-2 bg-[var(--admin-input-bg)] border border-[var(--admin-border-secondary)] hover:border-[var(--admin-text-muted)] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors text-white">
                Create Narrative
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
